import { useTranslation } from 'react-i18next';

import { useEffect, useState } from 'react';

import { Link, createFileRoute, useRouter, useSearch } from '@tanstack/react-router';

import {
  Anchor,
  Button,
  Checkbox,
  Container,
  Group,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@optiro/ui';

import { useSignup } from '@/hooks/useSignup/useSignup';
import { track } from '@/modules/Analytics';
import { useAuth } from '@/providers/AuthProvider/AuthProvider';

export const Route = createFileRoute('/experiences/signup')({
  component: SignupPage,
  validateSearch: (search: Record<string, unknown>) => ({
    email: typeof search.email === 'string' ? search.email : undefined,
    registrationContext:
      typeof search.registrationContext === 'string' ? search.registrationContext : undefined,
    returnUrl: typeof search.returnUrl === 'string' ? search.returnUrl : undefined,
  }),
});

export function SignupPage() {
  const { t } = useTranslation('signup');
  const {
    email: emailParam,
    registrationContext,
    returnUrl,
  } = useSearch({ from: '/experiences/signup' });
  const router = useRouter();
  const signup = useSignup();
  const { login } = useAuth();

  const [email, setEmail] = useState(emailParam ?? '');
  const [step, setStep] = useState<1 | 2>(emailParam ? 2 : 1);
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState(() => {
    if (!emailParam) return '';
    const domain = emailParam.split('@')[1]?.split('.')[0] ?? '';
    return domain.charAt(0).toUpperCase() + domain.slice(1);
  });
  const [title, setTitle] = useState('');
  const [password, setPassword] = useState('');
  const [newsletterOptedIn, setNewsletterOptedIn] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [dpaAccepted, setDpaAccepted] = useState(false);
  const [subscriptionTermsAccepted, setSubscriptionTermsAccepted] = useState(false);

  useEffect(() => {
    track('signup_started');
  }, []);

  const handleStep1Submit = (e: React.FormEvent) => {
    e.preventDefault();
    const domain = email.split('@')[1]?.split('.')[0] ?? '';
    setCompanyName(domain.charAt(0).toUpperCase() + domain.slice(1));
    setStep(2);
  };

  if (step === 1) {
    return (
      <Container size="xs" py="xl">
        <Paper p="xl" radius="md" withBorder shadow="sm">
          <form onSubmit={handleStep1Submit}>
            <Stack gap="md">
              <Title order={2} ta="center">
                {t('step1.title')}
              </Title>

              <TextInput
                label={t('step1.emailLabel')}
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                autoFocus
              />

              <Button type="submit" fullWidth size="lg" loading={signup.isPending}>
                {t('step1.submitButton')}
              </Button>

              <Group justify="center" gap="xs">
                <Text size="sm" c="dimmed">
                  {t('step1.alreadyHaveAccount')}
                </Text>
                <Link to="/experiences/login">
                  <Anchor size="sm" fw={500}>
                    {t('step1.logIn')}
                  </Anchor>
                </Link>
              </Group>
            </Stack>
          </form>
        </Paper>
      </Container>
    );
  }

  const handleStep2Submit = (e: React.FormEvent) => {
    e.preventDefault();
    signup.mutate(
      {
        email,
        name,
        title,
        company_name: companyName,
        password,
        newsletter_opted_in: newsletterOptedIn,
        terms_accepted: termsAccepted,
        privacy_accepted: privacyAccepted,
        dpa_accepted: dpaAccepted,
        subscription_terms_accepted: subscriptionTermsAccepted,
        registration_context: registrationContext,
      },
      {
        onSuccess: async () => {
          track('signup_completed', { origin: 'organic' });
          await login(email, password);
          router.navigate({ to: returnUrl ?? '/app/dashboard' });
        },
      }
    );
  };

  return (
    <Container size="xs" py="xl">
      <Paper p="xl" radius="md" withBorder shadow="sm">
        <form onSubmit={handleStep2Submit}>
          <Stack gap="md">
            <Title order={2} ta="center">
              {t('step2.title')}
            </Title>

            <Group justify="center" gap={4}>
              <Text size="sm" c="dimmed">
                {t('step2.creatingFor')} <strong>{email}</strong>
              </Text>
              <Anchor size="sm" onClick={() => setStep(1)} style={{ cursor: 'pointer' }}>
                {t('step2.changeEmail')}
              </Anchor>
            </Group>

            <TextInput
              label={t('step2.nameLabel')}
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <TextInput
              label={t('step2.titleLabel')}
              required
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
            />
            <TextInput
              label={t('step2.companyNameLabel')}
              required
              value={companyName}
              onChange={(e) => setCompanyName(e.currentTarget.value)}
            />
            <PasswordInput
              label={t('step2.passwordLabel')}
              required
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
            />
            <Checkbox
              required
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.currentTarget.checked)}
              label={
                <Text size="sm">
                  {t('step2.termsPrefix')}{' '}
                  <Anchor
                    href={import.meta.env.VITE_LANDING_PAGE_URL + '/terms-and-conditions'}
                    target="_blank"
                    size="sm"
                  >
                    {t('step2.termsLink')}
                  </Anchor>
                </Text>
              }
            />
            <Checkbox
              required
              checked={privacyAccepted}
              onChange={(e) => setPrivacyAccepted(e.currentTarget.checked)}
              label={
                <Text size="sm">
                  {t('step2.privacyPrefix')}{' '}
                  <Anchor
                    href={import.meta.env.VITE_LANDING_PAGE_URL + '/privacy-policy'}
                    target="_blank"
                    size="sm"
                  >
                    {t('step2.privacyLink')}
                  </Anchor>
                </Text>
              }
            />
            <Checkbox
              required
              checked={dpaAccepted}
              onChange={(e) => setDpaAccepted(e.currentTarget.checked)}
              label={
                <Text size="sm">
                  {t('step2.dpaPrefix')}{' '}
                  <Anchor
                    href={import.meta.env.VITE_LANDING_PAGE_URL + '/dpa'}
                    target="_blank"
                    size="sm"
                  >
                    {t('step2.dpaLink')}
                  </Anchor>
                </Text>
              }
            />
            <Checkbox
              required
              checked={subscriptionTermsAccepted}
              onChange={(e) => setSubscriptionTermsAccepted(e.currentTarget.checked)}
              label={
                <Text size="sm">
                  {t('step2.subscriptionTermsPrefix')}{' '}
                  <Anchor
                    href={import.meta.env.VITE_LANDING_PAGE_URL + '/subscription-terms'}
                    target="_blank"
                    size="sm"
                  >
                    {t('step2.subscriptionTermsLink')}
                  </Anchor>
                </Text>
              }
            />
            <Checkbox
              label={t('step2.newsletterLabel')}
              checked={newsletterOptedIn}
              onChange={(e) => setNewsletterOptedIn(e.currentTarget.checked)}
            />

            <Button type="submit" fullWidth size="lg" loading={signup.isPending}>
              {t('step2.submitButton')}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}
